import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['submit'];

  connect() {
    this.enableIfValid();
    this.setupHeadieLegal();
    this.detectAutofill();
  }

  disconnect() {
    if (this.autofillTimeoutId) {
      clearTimeout(this.autofillTimeoutId);
      this.autofillTimeoutId = null;
    }

    if (this.autofillInputs) {
      this.autofillInputs.forEach((input) => {
        input.removeEventListener('animationstart', this.autofillAnimationStartHandler);
        input.removeEventListener('change', this.autofillChangeHandler);
      });

      this.autofillInputs = null;
      this.autofillAnimationStartHandler = null;
      this.autofillChangeHandler = null;
    }

    if (this.headieLegalCheckbox && this.headieLegalCheckboxHandler) {
      this.headieLegalCheckbox.removeEventListener(
        'change',
        this.headieLegalCheckboxHandler,
      );
      this.headieLegalCheckbox = null;
      this.headieLegalCheckboxHandler = null;
    }

    if (this.headieLegalObserver) {
      this.headieLegalObserver.disconnect();
      this.headieLegalObserver = null;
    }
  }

  detectAutofill() {
    this.autofillInputs = this.element.querySelectorAll('input');
    this.autofillAnimationStartHandler = (e) => {
      if (e.animationName === 'onAutoFillStart') {
        this.enableIfValid();
      }
    };
    this.autofillChangeHandler = () => {
      this.enableIfValid();
    };

    // Chrome/Edge: listen for the animation that fires on :-webkit-autofill
    this.autofillInputs.forEach((input) => {
      input.addEventListener('animationstart', this.autofillAnimationStartHandler);
    });

    // Safari/Firefox: these browsers may fire 'change' on autofill
    this.autofillInputs.forEach((input) => {
      input.addEventListener('change', this.autofillChangeHandler);
    });

    // Fallback: check after a short delay for browsers that don't fire events
    this.autofillTimeoutId = setTimeout(() => this.enableIfValid(), 300);
  }

  enableIfValid() {
    const { element, submitTarget } = this;
    element.checkValidity()
      ? submitTarget.removeAttribute('disabled')
      : submitTarget.setAttribute('disabled', true);
  }

  disable() {
    const { submitTarget } = this;
    submitTarget.setAttribute('disabled', true);
  }

  hideRevealEmail() {
    const { element } = this;
    const input = element.querySelectorAll('.hide-reveal')[0];
    const eyeOpen = element.querySelectorAll("button[id='eye-open']")[0];
    const eyeClosed = element.querySelectorAll("button[id='eye-closed']")[0];

    if (input.type === 'password') {
      input.type = 'text';
      eyeClosed.classList.add('hide');
      eyeOpen.classList.remove('hide');
      eyeOpen.focus();
    } else {
      input.type = 'password';
      eyeOpen.classList.add('hide');
      eyeClosed.classList.remove('hide');
      eyeClosed.focus();
    }
  }

  setupHeadieLegal() {
    const checkbox = document.getElementById('accepts_headie_legal');
    if (!checkbox) return;
    const oauthButtons = document.querySelectorAll(
      '#oauth-login-buttons button, #oauth-login-buttons a',
    );
    const loginButton = document.querySelector('[data-submit-target="submit"]');
    const usernameInput = document.querySelector(
      '#login-form input[name="user[username]"]',
    );
    const passwordInput = document.querySelector(
      '#login-form input[name="user[password]"]',
    );

    const handleCheckboxChange = () =>
      this.handleHeadieLegalCheckbox({
        checkbox,
        oauthButtons,
        loginButton,
        usernameInput,
        passwordInput,
      });

    this.headieLegalCheckbox = checkbox;
    this.headieLegalCheckboxHandler = handleCheckboxChange;
    checkbox.addEventListener('change', handleCheckboxChange);
    handleCheckboxChange();
    if (loginButton) {
      this.headieLegalObserver = this.observeLoginButton(loginButton, checkbox);
    }
  }

  handleHeadieLegalCheckbox({
    checkbox,
    oauthButtons,
    loginButton,
    usernameInput,
    passwordInput,
  }) {
    const disabled = !checkbox.checked;
    this.updateOauthButtons(oauthButtons, disabled);
    this.updateLoginButton(loginButton, usernameInput, passwordInput, disabled);
  }

  updateOauthButtons(oauthButtons, disabled) {
    oauthButtons.forEach((btn) => this.setOauthButtonState(btn, disabled));
  }

  updateLoginButton(loginButton, usernameInput, passwordInput, disabled) {
    if (!loginButton) return;
    const usernameValid = usernameInput && usernameInput.value.length >= 2;
    const passwordValid = passwordInput && passwordInput.value.length > 0;
    loginButton.disabled = !usernameValid || !passwordValid ? true : disabled;
  }

  observeLoginButton(loginButton, checkbox) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'disabled'
        ) {
          if (!checkbox.checked && !loginButton.disabled) {
            loginButton.disabled = true;
          }
        }
      });
    });
    observer.observe(loginButton, {
      attributes: true,
      attributeFilter: ['disabled'],
    });
    return observer;
  }

  setOauthButtonState(btn, disabled) {
    if (btn.tagName === 'BUTTON') {
      btn.disabled = disabled;
    } else if (btn.tagName === 'A') {
      if (disabled) {
        btn.classList.add('disabled');
        btn.setAttribute('tabindex', '-1');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.classList.remove('disabled');
        btn.removeAttribute('tabindex');
        btn.removeAttribute('aria-disabled');
      }
    }
  }
}
